/**
 * sanitize.ts — Utilitário de sanitização HTML para prevenção de XSS.
 *
 * Usar em todo dangerouslySetInnerHTML antes de renderizar HTML externo.
 *
 * @example
 *   dangerouslySetInnerHTML={{ __html: sanitizeHtml(contentHtml) }}
 *
 * Hardening [TMF9-60711]:
 *  - Hook `afterSanitizeAttributes` força `rel="noopener noreferrer"` em
 *    qualquer link com `target` (reverse tabnabbing / window.opener).
 *  - Propriedades CSS de overlay (position/z-index/top/left/...) são
 *    removidas do atributo `style` para barrar clickjacking via CSS overlay,
 *    preservando formatação tipográfica legítima (cor, fonte, alinhamento).
 *
 * Hardening [TMF9-60721]:
 *  - Hook `uponSanitizeElement` limpa o CONTEÚDO TEXTUAL de blocos `<style>`
 *    (usado por `sanitizeEmailHtml`, que permite a tag via `ADD_TAGS`) das
 *    mesmas propriedades de overlay/reposicionamento — o hook de atributo
 *    `style` não alcança CSS declarado dentro de `<style>...</style>`.
 */
import DOMPurify from 'dompurify';

/**
 * Allowlist de propriedades CSS cosméticas/tipográficas que NÃO conseguem
 * reposicionar nem sobrepor elementos (defesa contra clickjacking via CSS
 * overlay). Qualquer propriedade fora desta lista — vendor-prefixada
 * (`-webkit-*`), escapada (`pos\74ition`), standalone nova ou desconhecida —
 * é descartada automaticamente. Abordagem allowlist fecha a classe inteira de
 * bypass que uma blocklist não consegue (TMF9-60711 / security-review #921).
 *
 * EXCLUÍDAS de propósito: position, top/right/bottom/left, inset, margin,
 * transform, translate/rotate/scale, z-index, float, clip/clip-path,
 * width/height/max-min dimensions, opacity, pointer-events, mix-blend-mode,
 * filter, will-change, content, e todo vendor-prefix.
 */
const ALLOWED_STYLE_PROPS = new Set([
  // Tipografia / cor
  'color',
  'background-color',
  'font-family',
  'font-size',
  'font-weight',
  'font-style',
  'font-variant',
  'text-align',
  'text-decoration',
  'text-transform',
  'line-height',
  'letter-spacing',
  'white-space',
  'vertical-align',
  // Borda (cosmética — não reposiciona)
  'border',
  'border-top',
  'border-right',
  'border-bottom',
  'border-left',
  'border-color',
  'border-width',
  'border-style',
  'border-radius',
  // Espaçamento interno (padding é sempre positivo — não reposiciona overlay)
  'padding',
  'padding-top',
  'padding-right',
  'padding-bottom',
  'padding-left',
  // Listas (só bullet-style; list-style/list-style-image aceitam <url> → excluídos)
  'list-style-type',
  'list-style-position',
]);

/**
 * Decodifica escapes CSS de um trecho (nome OU valor): strip de comentário CSS
 * (o par abre-fecha de comentário — descrito em palavras porque a sequência
 * literal encerraria este próprio bloco), decode de escapes hex `\XX` (unicode)
 * e demais escapes com barra, e lowercase. Aplicar ANTES de qualquer
 * comparação/regex de segurança para que
 * `pos\74ition` → `position` e `\75rl(` → `url(` não escapem a inspeção.
 */
function decodeCssEscapes(input: string): string {
  return input
    .replace(/\/\*[\s\S]*?\*\//g, '') // remove comentários CSS
    .replace(/\\([0-9a-fA-F]{1,6})\s?/g, (_m, hex) => {
      const code = parseInt(hex, 16);
      return Number.isFinite(code) ? String.fromCodePoint(code) : '';
    })
    .replace(/\\(.)/g, '$1') // demais escapes com barra
    .toLowerCase();
}

/**
 * Normaliza o NOME de uma propriedade CSS antes de comparar com a allowlist.
 * Impede que `pos\74ition` escape como propriedade "desconhecida".
 */
function normalizeStyleProp(prop: string): string {
  return decodeCssEscapes(prop).trim();
}

/**
 * Sanitiza uma string de `style` inline por ALLOWLIST: mantém apenas
 * declarações cujo nome (normalizado) esteja em `ALLOWED_STYLE_PROPS` e cujo
 * valor não contenha vetores perigosos. Retorna a string saneada (pode ficar
 * vazia).
 */
function sanitizeStyleAttr(style: string): string {
  return style
    .split(';')
    .map((decl) => decl.trim())
    .filter(Boolean)
    .filter((decl) => {
      const idx = decl.indexOf(':');
      if (idx === -1) return false;
      const prop = normalizeStyleProp(decl.slice(0, idx));
      // Decode do VALOR com o MESMO tratamento do nome, para que escapes hex
      // (`\75rl(` → `url(`) não contornem o bloqueio de padrões perigosos.
      const value = decodeCssEscapes(decl.slice(idx + 1));
      // Allowlist: prop não reconhecida (vendor-prefix, escapada, nova) é descartada.
      if (!ALLOWED_STYLE_PROPS.has(prop)) return false;
      // Bloqueio de valor perigoso: expression(), javascript:, url() (info-leak/beacon), etc.
      if (/expression\s*\(|javascript:|-moz-binding|behavior\s*:|url\s*\(/.test(value)) return false;
      return true;
    })
    .join('; ');
}

/**
 * Propriedades CSS de overlay/reposicionamento bloqueadas dentro do
 * CONTEÚDO de um bloco `<style>` (regras `seletor { prop: valor }`).
 * Blocklist (não allowlist) porque aqui a superfície é regra de CSS
 * completa, não um `style=""` isolado — o mesmo conjunto barrado no
 * atributo inline (ver `ALLOWED_STYLE_PROPS`) é a referência de risco.
 */
const DANGEROUS_STYLE_TAG_PROPS = new Set([
  'position', 'top', 'right', 'bottom', 'left', 'inset',
  'z-index', 'transform', 'translate', 'rotate', 'scale',
  'float', 'clip', 'clip-path', 'opacity', 'pointer-events',
  'mix-blend-mode', 'filter', 'will-change', 'content',
  'width', 'height', 'max-width', 'max-height', 'min-width', 'min-height',
]);

/**
 * Sanitiza o texto de um bloco `<style>...</style>` removendo declarações
 * de propriedades de overlay/reposicionamento (clickjacking) e valores
 * perigosos (`expression()`, `javascript:`, `url()`, `-moz-binding`),
 * preservando o restante das regras CSS (seletores, cores, tipografia).
 */
function sanitizeStyleTagContent(css: string): string {
  return css.replace(/([a-zA-Z-]+)\s*:\s*([^;{}]+);?/g, (match, propRaw: string, valueRaw: string) => {
    const prop = normalizeStyleProp(propRaw);
    if (DANGEROUS_STYLE_TAG_PROPS.has(prop)) return '';
    const value = decodeCssEscapes(valueRaw);
    if (/expression\s*\(|javascript:|-moz-binding|behavior\s*:|url\s*\(/.test(value)) return '';
    return match;
  });
}

/**
 * Registra (uma única vez) o hook global que endurece os atributos após a
 * sanitização do DOMPurify. Idempotente — seguro sob HMR / múltiplos imports.
 */
let hookRegistered = false;
function registerSanitizeHooks(): void {
  if (hookRegistered) return;
  hookRegistered = true;

  DOMPurify.addHook('afterSanitizeAttributes', (node) => {
    // Reverse tabnabbing: qualquer link com target ganha noopener+noreferrer.
    // Faz MERGE de tokens para preservar rel pré-existente (nofollow/sponsored/ugc).
    if (node instanceof Element && node.hasAttribute('target')) {
      const tokens = new Set(
        (node.getAttribute('rel') ?? '')
          .split(/\s+/)
          .map((t) => t.trim())
          .filter(Boolean),
      );
      tokens.add('noopener');
      tokens.add('noreferrer');
      node.setAttribute('rel', Array.from(tokens).join(' '));
    }

    // Clickjacking: remove propriedades CSS de overlay do style inline.
    if (node instanceof Element && node.hasAttribute('style')) {
      const cleaned = sanitizeStyleAttr(node.getAttribute('style') ?? '');
      if (cleaned) {
        node.setAttribute('style', cleaned);
      } else {
        node.removeAttribute('style');
      }
    }
  });

  // Clickjacking via bloco <style>: sanitiza o CONTEÚDO textual da tag
  // (só relevante para sanitizeEmailHtml, que a permite via ADD_TAGS —
  // sanitizeHtml() já barra a tag inteira por não estar em ALLOWED_TAGS).
  DOMPurify.addHook('uponSanitizeElement', (node, data) => {
    if (data.tagName === 'style' && node.textContent) {
      node.textContent = sanitizeStyleTagContent(node.textContent);
    }
  });
}

registerSanitizeHooks();

/**
 * Sanitiza HTML removendo scripts, event handlers e outros vetores de XSS.
 * Mantém formatação segura (b, i, a, p, ul, ol, li, h1-h6, img com src seguro).
 * Links com `target` recebem `rel="noopener noreferrer"` automaticamente e o
 * atributo `style` tem propriedades de overlay removidas (ver hooks acima).
 */
export function sanitizeHtml(html: string | undefined | null): string {
  if (!html) return '';
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: [
      'a', 'b', 'blockquote', 'br', 'caption', 'cite', 'code', 'col',
      'colgroup', 'dd', 'del', 'details', 'div', 'dl', 'dt', 'em',
      'figcaption', 'figure', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
      'hr', 'i', 'img', 'ins', 'kbd', 'li', 'mark', 'ol', 'p', 'pre',
      'q', 's', 'samp', 'small', 'span', 'strong', 'sub', 'summary',
      'sup', 'table', 'tbody', 'td', 'tfoot', 'th', 'thead', 'time',
      'tr', 'u', 'ul', 'var',
    ],
    ALLOWED_ATTR: [
      'href', 'src', 'alt', 'title', 'class', 'id', 'width', 'height',
      'colspan', 'rowspan', 'align', 'valign', 'style', 'target', 'rel',
    ],
    ALLOWED_URI_REGEXP: /^(?:(?:(?:f|ht)tps?|mailto|tel|callto|cid|xmpp):|[^a-z]|[-a-z+.]+(?:[^-a-z+.:]|$))/i,
    FORCE_BODY: true,
  });
}

/**
 * Sanitiza HTML para contexto de email — permite mais tags de layout.
 * Beneficia-se dos mesmos hooks globais (rel=noopener, style sem overlay).
 */
export function sanitizeEmailHtml(html: string | undefined | null): string {
  if (!html) return '';
  return DOMPurify.sanitize(html, {
    WHOLE_DOCUMENT: false,
    FORCE_BODY: true,
    ADD_TAGS: ['style'],
    FORBID_TAGS: ['script', 'object', 'embed', 'form', 'input', 'button'],
    FORBID_ATTR: ['onerror', 'onload', 'onclick', 'onmouseover', 'onfocus', 'onblur'],
  });
}
